import { useState, useRef, useCallback } from "react";
import { URL_CONSTANTS } from "../../axios/urlLinksThemes";
import { ICATEGORY, IRESAllData } from "../../shared/commonUtils";
import { useDispatch } from "react-redux";
import { httpPostWithAuth } from "../../axios/axiosUtils";
import {
  getBussinessData,
  getUserPrefersLang,
  TOKEN_EXPIRIES_MESSAGE,
} from "../../shared/helpersMethod";
import { addHomePageCategories } from "../../store/slices/categories-nav-menu";
import useGetToken from "../useGetToken";

const useGetHomeCategory = () => {
  const dispatch = useDispatch();
  const { getToken } = useGetToken();
  const [loading, setLoading] = useState(false);
  const [homePageCategories, setHomePageCategories] = useState<ICATEGORY[]>([]);
  const [mobileMenu, setMobileMenu] = useState<ICATEGORY[]>([]);
  const userLanguage = getUserPrefersLang();
  const queryParamLang = `?lang=${userLanguage}`;

  // Prevent multiple simultaneous API calls
  const apiCallInProgress = useRef(false);
  const dataFetched = useRef(false);
  const mobileDataFetched = useRef(false);

  // Helper function to safely check if businessRedisData exists
  const getBusinessRedisData = () => {
    console.log("headerCategoriesRedisData", window.headerCategoriesRedisData);
    try {
      if (
        typeof window !== "undefined" &&
        window.headerCategoriesRedisData &&
        window.headerCategoriesRedisData !== null &&
        window.headerCategoriesRedisData !== undefined &&
        window.headerCategoriesRedisData !== "__businessHeaderCategories__" &&
        typeof window.headerCategoriesRedisData === "object"
      ) {
        return window.headerCategoriesRedisData;
      }
    } catch (error) {
      console.warn("Error accessing headerCategoriesRedisData:", error);
    }
    return null;
  };

  // Helper function to safely check if mobile categories Redis data exists
  const getMobileCategoriesRedisData = () => {
    console.log("mobileCategoriesRedisData", window.mobileCategoriesRedisData);
    try {
      if (
        typeof window !== "undefined" &&
        window.mobileCategoriesRedisData &&
        window.mobileCategoriesRedisData !== null &&
        window.mobileCategoriesRedisData !== undefined &&
        window.mobileCategoriesRedisData !==
          "__businessMobileHeaderCategories__" &&
        typeof window.mobileCategoriesRedisData === "object"
      ) {
        return window.mobileCategoriesRedisData;
      }
    } catch (error) {
      console.warn("Error accessing mobileCategoriesRedisData:", error);
    }
    return null;
  };

  const getHomePageCategory = useCallback(
    async (
      test: string = "",
      isMobile: string = "",
      retryAfterTokenRefresh = false
    ): Promise<void> => {
      // Prevent multiple simultaneous calls (but allow retry after token refresh)
      if (apiCallInProgress.current && !retryAfterTokenRefresh) {
        console.log("Categories API call already in progress, skipping");
        return;
      }

      // Skip Redis data if this is a retry after token refresh
      if (!retryAfterTokenRefresh) {
        // Check for cached Redis data first
        const headerCategoriesRedisData = getBusinessRedisData();
        if (headerCategoriesRedisData && !dataFetched.current) {
          console.log("Using cached Redis categories data");
          try {
            setHomePageCategories(headerCategoriesRedisData);
            dispatch(addHomePageCategories(headerCategoriesRedisData));
            dataFetched.current = true;
            return;
          } catch (error) {
            console.error("Error processing Redis categories data:", error);
            // Continue to API call if Redis data processing fails
          }
        }
      }

      // Get business token
      let bussinessData = getBussinessData();
      if (!bussinessData?.accessToken) {
        console.log(
          "No business token found for categories, attempting to get token"
        );
        try {
          const newToken = await getToken();
          // Update business token after getting new token
          bussinessData = getBussinessData();
          if (!bussinessData?.accessToken) {
            console.error("Still no token after getToken call for categories");
            return;
          }
        } catch (error) {
          console.error("Failed to get token for categories:", error);
          return;
        }
      }

      apiCallInProgress.current = true;
      setLoading(true);

      try {
        console.log("🚀 Making API call to get categories data");
        const res: IRESAllData = await httpPostWithAuth(
          URL_CONSTANTS.get("URL_Get_Categories")! +
            `${userLanguage !== null ? queryParamLang : ""}`,
          {},
          bussinessData.accessToken
        );

        if (res.type === "error" && res?.token === "expired") {
          console.log(
            "🔄 Categories token expired, refreshing token and retrying"
          );
          try {
            // Reset the API call flag to allow retry
            apiCallInProgress.current = false;

            const newToken = await getToken(true); // Force token refresh
            console.log(
              "✅ New token obtained for categories:",
              newToken ? "Success" : "Failed"
            );

            // Retry the API call with new token
            if (newToken) {
              console.log("🔄 Retrying categories API call with new token");
              return getHomePageCategory(test, isMobile, true); // Pass true to indicate this is a retry
            } else {
              throw new Error("Failed to get new token for categories");
            }
          } catch (tokenError) {
            console.error(
              "❌ Failed to refresh token for categories:",
              tokenError
            );
            throw tokenError;
          }
        }

        if (res.type === "error") {
          console.error("❌ Categories API returned error:", res.message);
          return;
        }

        if (res) {
          console.log("✅ Successfully fetched categories data");
          setHomePageCategories(res);
          dispatch(addHomePageCategories(res));
          dataFetched.current = true;
        }
      } catch (error: any) {
        console.error("❌ Categories API call failed:", error.message);
      } finally {
        setLoading(false);
        apiCallInProgress.current = false;
      }
    },
    [getToken, userLanguage, queryParamLang, dispatch]
  );

  const getMobileMenu = useCallback(
    async (
      test: string = "",
      isMobile: string = "",
      retryAfterTokenRefresh = false
    ): Promise<void> => {
      // Prevent multiple simultaneous calls (but allow retry after token refresh)
      if (apiCallInProgress.current && !retryAfterTokenRefresh) {
        console.log("Mobile menu API call already in progress, skipping");
        return;
      }

      // Skip Redis data if this is a retry after token refresh
      if (!retryAfterTokenRefresh) {
        // Check for cached Redis data first
        const mobileCategoriesRedisData = getMobileCategoriesRedisData();
        if (mobileCategoriesRedisData && !mobileDataFetched.current) {
          console.log("Using cached Redis mobile categories data");
          try {
            setMobileMenu(mobileCategoriesRedisData);
            mobileDataFetched.current = true;
            return;
          } catch (error) {
            console.error(
              "Error processing Redis mobile categories data:",
              error
            );
            // Continue to API call if Redis data processing fails
          }
        }
      }

      // Get business token
      let bussinessData = getBussinessData();
      if (!bussinessData?.accessToken) {
        console.log(
          "No business token found for mobile menu, attempting to get token"
        );
        try {
          const newToken = await getToken();
          // Update business token after getting new token
          bussinessData = getBussinessData();
          if (!bussinessData?.accessToken) {
            console.error("Still no token after getToken call for mobile menu");
            return;
          }
        } catch (error) {
          console.error("Failed to get token for mobile menu:", error);
          return;
        }
      }

      apiCallInProgress.current = true;
      setLoading(true);

      try {
        console.log("🚀 Making API call to get mobile menu data");
        const res: IRESAllData = await httpPostWithAuth(
          URL_CONSTANTS.get("URL_Get_Categories")! +
            `${userLanguage !== null ? queryParamLang : ""}&mobileMenu=Y`,
          {},
          bussinessData.accessToken
        );

        if (res.type === "error" && res?.token === "expired") {
          console.log(
            "🔄 Mobile menu token expired, refreshing token and retrying"
          );
          try {
            // Reset the API call flag to allow retry
            apiCallInProgress.current = false;

            const newToken = await getToken(true); // Force token refresh
            console.log(
              "✅ New token obtained for mobile menu:",
              newToken ? "Success" : "Failed"
            );

            // Retry the API call with new token
            if (newToken) {
              console.log("🔄 Retrying mobile menu API call with new token");
              return getMobileMenu(test, isMobile, true); // Pass true to indicate this is a retry
            } else {
              throw new Error("Failed to get new token for mobile menu");
            }
          } catch (tokenError) {
            console.error(
              "❌ Failed to refresh token for mobile menu:",
              tokenError
            );
            throw tokenError;
          }
        }

        if (res.type === "error") {
          console.error("❌ Mobile menu API returned error:", res.message);
          return;
        }

        if (res && res.type !== "error") {
          console.log("✅ Successfully fetched mobile menu data");
          setMobileMenu(res);
          mobileDataFetched.current = true;
        }
      } catch (error: any) {
        console.error("❌ Mobile menu API call failed:", error.message);
      } finally {
        setLoading(false);
        apiCallInProgress.current = false;
      }
    },
    [getToken, userLanguage, queryParamLang]
  );

  return {
    homePageCategories,
    getHomePageCategory,
    mobileMenuLoading: loading,
    mobileMenu,
    getMobileMenu,
  };
};

export default useGetHomeCategory;
